import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { ExclamationCircleOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

import { ImagePaths } from "../../utils/ImagePaths";
import { LOGIN_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import appLogo from "../../assets/png/HRZyra-Logo.png";

// ------------------- COMPONENTS -------------------

const ConfirmModal = ({ open, onConfirm, onCancel }) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-5 w-[25rem] max-w-md animate-fadeIn transition-colors">
                {/* Heading */}
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                    Already Logged In <ExclamationCircleOutlined />
                </h2>

                {/* Message */}
                <p className="text-[13px] text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
                    This account is already active on another device/session. <br />
                    Do you want to continue and force this login?
                </p>

                {/* Actions */}
                <div className="flex justify-end gap-3 text-sm">
                    <button
                        onClick={onCancel}
                        className="py-2 px-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 
                       dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 
                       transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="py-2 px-3 rounded-lg text-white bg-[#04b7b1] hover:bg-[#04b7b1]/80 
                       focus:ring-2 focus:ring-[#04b7b1] focus:ring-offset-2 
                       focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 
                       transition"
                    >
                        Yes, Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

const TypingText = ({ texts }) => {
    const [text, setText] = useState("");
    const [index, setIndex] = useState(0);
    const [subIndex, setSubIndex] = useState(0);
    const [reverse, setReverse] = useState(false);

    useEffect(() => {
        if (index === texts.length) return;

        if (subIndex === texts[index].length + 1 && !reverse) {
            setTimeout(() => setReverse(true), 1500);
            return;
        }
        if (subIndex === 0 && reverse) {
            setReverse(false);
            setIndex((prev) => (prev + 1) % texts.length);
            return;
        }

        const timeout = setTimeout(() => {
            setSubIndex((prev) => prev + (reverse ? -1 : 1));
        }, reverse ? 50 : 100);

        setText(texts[index].substring(0, subIndex));
        return () => clearTimeout(timeout);
    }, [subIndex, index, reverse, texts]);

    return (
        <h2 className="lg:text-2xl md:text-lg font-medium text-white text-center min-h-[32px]">
            {text}
            <span className="animate-pulse">|</span>
        </h2>
    );
};

const PasswordInput = ({ value, onChange }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="relative">
            <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
            <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={value}
                onChange={onChange}
                className="w-full px-2 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-[#04b7b1] transition"
                placeholder="Enter your password"
            />
            <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-8 text-gray-500 hover:text-[#04b7b1] text-sm"
            >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
        </div>
    );
};

// ------------------- MAIN LOGIN -------------------
const Login = () => {
    const navigate = useNavigate();
    const { loginauth, isAuthenticated } = useContext(AuthContext);
    const { create } = useApiMethods();

    const [loading, setLoading] = useState(false);
    const [keepSignedIn, setKeepSignedIn] = useState(false);
    const [values, setValues] = useState({ username: "", password: "" });
    const [showModal, setShowModal] = useState(false);
    const [forceLoginValues, setForceLoginValues] = useState(null);

    useEffect(() => {
        if (isAuthenticated) {
            // Org admin with branches -> branch selection first; else home.
            const branchAvail = sessionStorage.getItem("branch_avail") === "true";
            navigate(branchAvail ? "/branches" : "/home");
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) => setValues({ ...values, [e.target.name]: e.target.value });

    const handleLogin = async (payload, forced = false) => {
        try {
            const response = await create(LOGIN_URL, payload, false, setLoading);

            if (response?.status === "success") {
                loginauth(response.data, keepSignedIn);
                // Org admin with branches -> land on branch selection; else home.
                navigate(response.data?.branch_avail ? "/branches" : "/home");
            } else if (response?.status === "fail") {
                if (response.message === "User already logged in" && !forced) {
                    setForceLoginValues({ ...payload, allow_login: true });
                    setShowModal(true);
                } else {
                    ToastMsg("error", response.message);
                }
            }
        } catch (error) {
            ToastMsg("error", error.message || "Something went wrong");
        }
    };

    const onFinish = (e) => {
        e.preventDefault();
        handleLogin(values);
    };

    const handleForceLogin = () => {
        setShowModal(false);
        handleLogin(forceLoginValues, true);
    };

    return (
        <div className="h-screen grid grid-cols-12 font-sans bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            {/* Left Side - Hidden on medium screens and below */}
            <div className="hidden lg:flex col-span-5 animated-gradient text-white flex-col justify-center items-center p-10 animate-gradient bg-[length:400%_400%] relative overflow-hidden">
                <img
                    src="https://cdn-icons-png.flaticon.com/512/3135/3135675.png"
                    alt="HRMS Illustration"
                    className="absolute opacity-10 w-[30%] h-[30%] object-contain"
                />
                <TypingText
                    texts={[
                        "Welcome to HRZyra",
                        "Your smarter way to manage HR & Payroll",
                        "Simplify attendance, payroll, and compliance",
                        "Empowering employees with smarter HR tools",
                        "Seamless HR solutions for growing businesses",
                    ]}
                />
            </div>

            {/* Right Side - Full width on medium and below, 7 columns on large and above */}
            <div className="col-span-full lg:col-span-7 relative flex items-center justify-center p-6">
                <div className="absolute inset-0 bg-[url('https://media.istockphoto.com/id/1847571768/photo/customer-with-bank-manager-in-office-stock-photo.jpg?s=1024x1024&w=is&k=20&c=Y3oAUjmWSEipuQoTva74TkZuZPYSwT9TFsMz-z1R0Kw=')] bg-cover bg-center opacity-20"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-sm dark:bg-black/50"></div>

                <div className="w-full max-w-sm relative z-10">
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                        <img src={appLogo} alt="App Logo" className="h-10 w-auto animate-fadeIn" />
                    </div>

                    {/* Card */}
                    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl p-6 animate-fadeIn">
                        <h2 className="text-center text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Sign In</h2>
                        <p className="text-center text-[13px] text-gray-500 dark:text-gray-400 mb-5">
                            Welcome back! Please enter your details.
                        </p>

                        <form onSubmit={onFinish} className="space-y-4">
                            {/* Username */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={values.username}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    autoFocus
                                    placeholder="Enter your username"
                                    className="w-full px-2 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-[#04b7b1] transition"
                                />
                            </div>

                            {/* Password */}
                            <PasswordInput value={values.password} onChange={handleChange} />

                            {/* Remember Me + Forgot */}
                            <div className="flex items-center justify-between">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={keepSignedIn}
                                        onChange={(e) => setKeepSignedIn(e.target.checked)}
                                        className="h-4 w-4 accent-[#04b7b1]/80 rounded border-gray-300 focus:ring-[#04b7b1]"
                                    />
                                    <span className="text-[13px] text-gray-600 dark:text-gray-300">Remember Me</span>
                                </label>
                                <Link
                                    to="/forgotpassword"
                                    className="text-[13px] text-[#04b7b1] hover:text-[#04b7b1]/80 font-medium"
                                >
                                    Forgot Password?
                                </Link>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full py-2 px-4 rounded-lg text-white font-semibold transition ${loading
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-[#04b7b1] hover:bg-[#04b7b1]/80 focus:ring-2 focus:ring-[#04b7b1] focus:ring-offset-2"
                                    }`}
                            >
                                {loading ? "Signing In..." : "Sign In"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Confirm Modal */}
            <ConfirmModal open={showModal} onConfirm={handleForceLogin} onCancel={() => setShowModal(false)} />
        </div>
    );
};

export default Login;
